import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import { type Classroom, type TeacherAccount } from '../../../accounts';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'teacher',
    updatePassword
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-7315">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-7316">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-7318">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7319" /> Bewerk
              </Button> : null} data-id="element-7317">
          <div className="space-y-6" data-id="element-7320">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7321">
                <div data-id="element-7322">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7323">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-7324" />
                </div>
                <div data-id="element-7325">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7326">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-7327" />
                </div>
                <div className="sm:col-span-2" data-id="element-7328">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7329">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-7330" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-7331">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-7332">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-7333">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-7334">
                  <div data-id="element-7335">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7336">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7337">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-7338">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7339">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-7340">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-7341">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-7342">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-7343">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-7344">
                      <School className="w-5 h-5" data-id="element-7345" />
                    </div>
                    <div data-id="element-7346">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-7347">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-7348">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-7350">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-7351" /> Wijzig
              </Button> : null} data-id="element-7349">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-7352">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7353">
                <div className="sm:col-span-2" data-id="element-7354">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7355">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-7356" />
                </div>
                <div data-id="element-7357">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7358">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-7359" />
                </div>
                <div data-id="element-7360">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7361">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-7362" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-7363">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7364" />
                  <p data-id="element-7365">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-7366">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-7367" />
                  <p data-id="element-7368">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-7369">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-7370">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-7371">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-7372">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Klassen Overzicht */}
        <SettingsSection title="Klassen Overzicht" description="Je actieve klassen" icon={Users} accentColor="bg-blue-100 text-blue-600" action={<Button onClick={() => onNavigate?.('management')} variant="ghost" size="sm" data-id="element-7374">
              Beheer klassen
            </Button>} data-id="element-7373">
          {classrooms.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7375">
              {classrooms.map(c => <div key={c.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-7376">
                  <div className="font-bold text-slate-900" data-id="element-7377">{c.name}</div>
                  <div className="text-sm text-slate-500 mt-1" data-id="element-7378">
                    Groep {c.groupLevel} • {c.learnerIds.length} leerlingen
                  </div>
                </div>)}
            </div> : <div className="text-sm text-slate-500" data-id="element-7379">
              Je hebt nog geen klassen.
            </div>}
        </SettingsSection>
      </div>
    </PageLayout>;
}